import React, { useState, useEffect, useCallback } from 'react';
import { markersAPI, qrAPI } from '../../services/api';
import MapViewer from '../maps/MapViewer';
import MarkerCreationModal from '../maps/MarkerCreationModal';
import { MapPin, Eye, Calendar, Copy, QrCode, Check, RefreshCw, PenLine, X } from 'lucide-react';
import './MapDetailModal.css';

const MapDetailModal = ({ map, isOpen, onClose }) => {
  const [markers, setMarkers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showMarkerCreation, setShowMarkerCreation] = useState(false);
  const [copied, setCopied] = useState(false);

  const fetchMarkers = useCallback(async () => {
    if (!map) return;

    try {
      setLoading(true);
      setError(null);

      console.log('🔄 Fetching markers for map:', map.id);
      const response = await markersAPI.getMarkers(map.id);
      const markersData = response.data.data.markers;
      setMarkers(markersData);

      console.log('✅ Markers loaded for map:', markersData.length);
    } catch (error) {
      console.error('❌ Error loading markers:', error);
      setError('Failed to load markers');
    } finally {
      setLoading(false);
    }
  }, [map]);

  useEffect(() => {
    if (isOpen && map) {
      fetchMarkers();
    }
  }, [isOpen, map, fetchMarkers]);

  const handleMarkerClick = useCallback((markerData) => {
    console.log('Marker clicked:', markerData);
  }, []);

  const handleCopyShareUrl = useCallback(() => {
    if (map?.shareUrl) {
      navigator.clipboard.writeText(map.shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [map?.shareUrl]);

  const handleDownloadQR = useCallback(() => {
    if (map?.base44Code) {
      window.open(qrAPI.getQRCodeUrl(map.base44Code, { size: 400, download: true }), '_blank');
    }
  }, [map?.base44Code]);

  const handleAddMarkers = useCallback(() => {
    setShowMarkerCreation(true);
  }, []);

  const handleMarkersUpdated = useCallback(() => {
    console.log('🔄 Markers updated, refreshing...');
    fetchMarkers();
  }, [fetchMarkers]);

  if (!isOpen) return null;

  return (
    <div className="mdm-overlay" onClick={onClose}>
      <div className="mdm-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="mdm-header">
          <div className="mdm-header-left">
            <div className="mdm-title-row">
              <h2 className="mdm-title">{map.title}</h2>
              <span className={`mdm-status-badge ${map.isCurrentlyActive ? 'active' : 'inactive'}`}>
                <span className="mdm-status-dot" />
                {map.isCurrentlyActive ? 'Active' : 'Inactive'}
              </span>
            </div>
            {map.description && (
              <p className="mdm-description">{map.description}</p>
            )}
          </div>
          <button className="mdm-close-btn" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {/* Stats strip */}
        <div className="mdm-stats-strip">
          <div className="mdm-stat">
            <Eye size={15} className="mdm-stat-icon" />
            <span className="mdm-stat-value">{map.visitCount || 0}</span>
            <span className="mdm-stat-label">visits</span>
          </div>
          <div className="mdm-stat-divider" />
          <div className="mdm-stat">
            <MapPin size={15} className="mdm-stat-icon" />
            <span className="mdm-stat-value">{markers.length}</span>
            <span className="mdm-stat-label">markers</span>
          </div>
          <div className="mdm-stat-divider" />
          <div className="mdm-stat">
            <Calendar size={15} className="mdm-stat-icon" />
            <span className="mdm-stat-value">{new Date(map.startTime).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>
          </div>
        </div>

        {/* Map viewer */}
        <div className="mdm-map-area">
          {loading ? (
            <div className="mdm-loading">
              <div className="mdm-spinner" />
              <span>Loading markers…</span>
            </div>
          ) : error ? (
            <div className="mdm-error">
              <p>{error}</p>
              <button onClick={fetchMarkers} className="mdm-retry-btn">
                <RefreshCw size={14} />
                Retry
              </button>
            </div>
          ) : (
            <MapViewer
              mapData={map}
              markers={markers}
              height="420px"
              zoom={16}
              showUserLocation={true}
              onMarkerClick={handleMarkerClick}
            />
          )}
        </div>

        {/* Markers list */}
        {markers.length > 0 && (
          <div className="mdm-markers-section">
            <p className="mdm-markers-heading">
              <MapPin size={13} />
              Markers on this map
            </p>
            <div className="mdm-markers-grid">
              {markers.map(marker => (
                <div key={marker.id} className="mdm-marker-chip">
                  <span className="mdm-marker-category">{marker.category}</span>
                  <span className="mdm-marker-name">{marker.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer actions */}
        <div className="mdm-footer">
          <button className="mdm-btn mdm-btn-edit" onClick={handleAddMarkers}>
            <PenLine size={15} />
            Edit Markers
          </button>
          <div className="mdm-footer-right">
            <button className="mdm-btn mdm-btn-ghost" onClick={handleCopyShareUrl}>
              {copied ? <Check size={15} /> : <Copy size={15} />}
              {copied ? 'Copied!' : 'Share URL'}
            </button>
            <button className="mdm-btn mdm-btn-ghost" onClick={handleDownloadQR}>
              <QrCode size={15} />
              QR Code
            </button>
            <button className="mdm-btn mdm-btn-primary" onClick={onClose}>
              Done
            </button>
          </div>
        </div>

        {/* Marker Creation Modal */}
        <MarkerCreationModal
          isOpen={showMarkerCreation}
          onClose={() => setShowMarkerCreation(false)}
          mapData={map}
          onMarkersUpdated={handleMarkersUpdated}
        />
      </div>
    </div>
  );
};

export default MapDetailModal;